import React, { useState } from 'react';
import { StatusIndicator } from "@arctheme-components/elements/StatusIndicator/StatusIndicator";
import { EcommFindInStore } from "@arctheme-components/ecommerce/FindInstore/FindInstore";
import { useSyncExternalStore } from "react";
import { findinstore } from "../js/alpine/components/findinstore";
import { hasher } from "../js/alpine/stores/hasher";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import ProductAccordion from "@project-stories/ecommerce/Product/ProductAccordion/ProductAccordion";
import useMobileBreakpoint from "../src/hooks/useMobileBreakpoint";
import ProductGallery from "@project-stories/ecommerce/Product/ProductGallery/ProductGallery";
import ProductMain from "@project-stories/ecommerce/Product/ProductMain";
import ProductSwatches from "@project-stories/ecommerce/Product/ProductSwatches/ProductSwatches";
import ProductSuitKit from "@src/stories/ecommerce/Product/ProductSuitKit";
import ProductFindInStoreModal from "@src/stories/ecommerce/Product/ProductFindInStoreModal";
import ProductCombinedStoreValidator from "@src/stories/ecommerce/Product/ProductCombinedStoreValidator";
import QuickViewModal from "@src/stories/ecommerce/Product/QuickViewModal/QuickViewModal";

export function ProductTileComponent({ ...props }) {
  return (
    <ProductTile
      product={props.product}
      siblings={props.related_products || []}
      show_add={false}
    />
  );
}

export function ProductStockStatus({ ...props }) {
  return (
    <StatusIndicator
      message={"In Stock"}
      status={"success"}
      show_indicator={props.settings.show_indicator}
    />
  );
}

export function PDPMainComponent({ ...props }) {
  // console.log(props);
  return <ProductMain settings={props.settings} />;
}

export function ProductSwatchesComponent({ ...props }) {
  const swatches = props.settings.swatches;
  const currentColor = props.settings.current_colour;

  return (
    <div className={"product__swatches-component"}>
      <ProductSwatches
        swatches={swatches}
        currentColor={currentColor}
      />
    </div>
  );
}

export function ProductGalleryComponent({ ...props }) {
  const isMobile = useMobileBreakpoint("1024px");
  const [isGalleryReady, setIsGalleryReady] = useState(false);

  const { globals, section } = props.settings;
  let productImages = [];

  const videoQuality = section.video_quality;
  props.settings.additional.map((image, index) =>
    productImages.push({ url: image.src, alt: image.alt }),
  );

  // Custom code for MJ Bale to remove swatch images from rendering in gallery
  const filteredImages = productImages.filter(
    (image) => !image.url.match(/_s_|_s(\.|$)/i), // 'i' flag makes the regex case-insensitive
  );

  const videoUrl = props.settings.video_url
    ? `https://player.vimeo.com/video/${props.settings.video_url}?autoplay=1&quality=${videoQuality}p&loop=1&autopause=0&background=1`
    : null;

  // Hide skeleton when gallery is ready
  React.useEffect(() => {
    if (isGalleryReady) {
      const skeleton = document.getElementById('gallery-skeleton');
      if (skeleton) {
        skeleton.classList.add('hidden');
      }
    }
  }, [isGalleryReady]);

  // Track when carousel/gallery is initialized
  React.useEffect(() => {
    const handleFlickityReady = () => {
      setIsGalleryReady(true);
    };
    
    // Listen for proper Flickity initialization
    window.addEventListener('flickity-carousel-ready', handleFlickityReady);
    
    return () => {
      window.removeEventListener('flickity-carousel-ready', handleFlickityReady);
    };
  }, []);

  return (
    <>
      {!isMobile && (
        <div className={"product-gallery"}>
          <ProductGallery
            images={filteredImages}
            galleryType={props.settings.section.gallery_type}
            videoUrl={videoUrl}
            isGiftCard={props.settings.is_gift_card}
          />
        </div>
      )}
      {isMobile && (
        <div className={"product-gallery"}>
          <ProductGallery
            images={filteredImages}
            galleryType={"carousel"}
            videoUrl={videoUrl}
            isGiftCard={props.settings.is_gift_card}
          />
        </div>
      )}
    </>
  );
}

export function FindInStoreBlock({ ...props }) {
  // console.log("Find in Store Block SETUP");
  async function storeStockCheck() {
    // console.log("Finding Store Stock");
    findinstore.component().sku = props.settings.product_sku;

    await hasher.store().dispatchHash();
    await findinstore
      .component()
      .getStockForProduct(props.settings.product_sku);
    await findinstore.component().getDeliveryMethodsShopify();
  }

  function useInventoryLevels() {
    return useSyncExternalStore(
      findinstore.component().subscribe,
      findinstore.component().getInventoryLevels,
    );
  }

  function useDeliveryMethods() {
    return useSyncExternalStore(
      findinstore.component().subscribe,
      findinstore.component().getDeliveryMethods,
    );
  }

  return (
    <>
      <EcommFindInStore
        title={"Find in Store"}
        description={
          "Please enter your postcode to check the delivery and collection options. Make your selection as you proceed through the checkout."
        }
        buttonText={"Search"}
        clickEvent={storeStockCheck}
        inventoryLevels={useInventoryLevels()}
        deliveryMethods={useDeliveryMethods()}
      />
    </>
  );
}

export function Accordion({ ...props }) {
  const { block_settings, section_settings, product_sku } = props.settings;

  const parseDetails = (details) => {
    // Function to strip HTML tags
    const stripHtmlTags = (html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    };

    // Split by newline first
    const detailLines = details.split("<br/>");

    let paragraphCount = 0;
    let styleNumberAppended = false;

    // Process each line based on whether it contains a semicolon
    return detailLines.map((item, index) => {
      if (item.includes(";")) {
        // If the line contains a semicolon, strip HTML tags and split by semicolons
        let cleanDetails = stripHtmlTags(item);
        return cleanDetails.split(";").map((subItem, subIndex) => (
            <li key={`${index}-${subIndex}`}>{subItem.trim()}</li>
        ));
      } else {
        // Otherwise, just return the line as is (with trimming)
        let cleanDetails = stripHtmlTags(item);
        let style_numbers = [];
        const tags = props.settings.product?.tags || [];
        const suiAp21Tags = tags.filter(tag => tag.startsWith("suiAp21Code:"));
        const matchingTrouserTags = tags.filter(tag => tag.startsWith("matchingTrouser:"));
        const matchingVestTags = tags.filter(tag => tag.startsWith("matchingVest:"));
        const ap21Tag = tags.find(tag => tag.startsWith("ap21Code:"));

        if (suiAp21Tags.length > 0) {
          // Collect all style numbers for suit kit products
          suiAp21Tags.forEach(tag => style_numbers.push(tag.replace("suiAp21Code:", "")));
          matchingTrouserTags.forEach(tag => style_numbers.push(tag.replace("matchingTrouser:", "")));
          matchingVestTags.forEach(tag => style_numbers.push(tag.replace("matchingVest:", "")));
        } else if (ap21Tag) {
          style_numbers.push(ap21Tag.replace("ap21Code:", ""));
        } else {
          style_numbers.push(product_sku.replace(/(.*)(\-\w*\d+)/, (match, p1) => p1));
        }

        if (paragraphCount === 0){
          cleanDetails = '<br />' + cleanDetails.replace('[sku]', style_numbers.join(", "));
        } else {
          cleanDetails = cleanDetails.replace('[sku]', style_numbers.join(", "));
        }

        if (block_settings?.show_style_number_code && !styleNumberAppended) {
          const tags = props.settings.product?.tags || [];
          const suiAp21Tags = tags.filter(tag => tag.startsWith("suiAp21Code:"));
          const matchingTrouserTags = tags.filter(tag => tag.startsWith("matchingTrouser:"));
          const matchingVestTags = tags.filter(tag => tag.startsWith("matchingVest:"));
          const ap21Tag = tags.find(tag => tag.startsWith("ap21Code:"));

          const allStyleNumbers = [];
          if (suiAp21Tags.length > 0) {
            suiAp21Tags.forEach(tag => allStyleNumbers.push(tag.replace("suiAp21Code:", "")));
            matchingTrouserTags.forEach(tag => allStyleNumbers.push(tag.replace("matchingTrouser:", "")));
            matchingVestTags.forEach(tag => allStyleNumbers.push(tag.replace("matchingVest:", "")));
            cleanDetails += `<br />Style Number: ${allStyleNumbers.join(", ")}`;
          } else if (ap21Tag) {
            cleanDetails += `<br />Style Number: ${ap21Tag.replace("ap21Code:", "")}`;
          }
          styleNumberAppended = true;
        }

        paragraphCount += 1
        return cleanDetails;
      }
    });
  };

  const sampleItems = [
    {
      title: block_settings.additional_info_1,
      content: parseDetails(block_settings.additional_content_1),
      icon: block_settings.additional_icon_1,
    },
    {
      title: block_settings.additional_info_2,
      content: block_settings.additional_content_2,
      icon: block_settings.additional_icon_2,
    },
    {
      title: block_settings.additional_info_3,
      content: block_settings.additional_content_3,
      icon: block_settings.additional_icon_3,
    },
  ];

  return <ProductAccordion items={sampleItems} show_header={false} />;
}

export function ProductSuitKitComponent({ ...props }) {
  let settings = {};
  try {
    if (typeof props.settings === "string") {
      // Sanitise string: escape invalid backslashes and encode URI components
      let sanitised = props.settings.replace(/\\(?!["\\/bfnrtu])/g, "\\\\");
      // Handle special characters like $ and :
      sanitised = sanitised.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
      settings = JSON.parse(sanitised);
    } else {
      settings = props.settings;
    }
  } catch (error) {
    console.error("Failed to parse settings in ProductSuitKitComponent:", error, props.settings);
    settings = {};
  }
  return (
    <ProductSuitKit
      settings={settings}
    />
  );
}

export function ProductFindInStoreModalComponent({ ...props }) {
  const product = JSON.parse(props.settings);

  return (
    <ProductFindInStoreModal
      settings={product}
    />
  );
}

export function ProductCombinedStoreValidatorComponent({ ...props }) {
  return (
    <ProductCombinedStoreValidator
      formName={props.settings.form_name}
      product={props.settings.product}
    />
  );
}

export function QuickViewModalComponent({ ...props }) {
  return <QuickViewModal settings={props.settings} />;
}
